import { useEffect, useMemo, useState } from 'react';
import Select from '@atlaskit/select';
import Textfield from '@atlaskit/textfield';
import { IDeliveryRequestFieldOptions } from 'core/api/dev-tools/dev-tools-api-interface';

/**
 * Dropdowns for the answers on a single request.
 *
 * Every one starts on "Scenario default", which leaves that answer to the
 * scenario — so choosing a scenario still means what it did, and a dropdown is
 * only ever a change on top of it. What is chosen is handed up as the same
 * overrides object the JSON box takes, keyed by the fixture's field names; the
 * JSON box still wins where both set a field.
 */

interface IOption {
  label: string;
  value: string;
}

const DEFAULT: IOption = { label: 'Scenario default', value: '' };

const withDefault = (values: string[]): IOption[] => [DEFAULT, ...values.map((value) => ({ label: value, value }))];

const YES_NO = withDefault(['Yes', 'No']);

const DAYS: IOption[] = [
  DEFAULT,
  { label: 'Today', value: '0' },
  { label: 'Tomorrow', value: '1' },
  { label: 'In 2 days', value: '2' },
  { label: 'In 3 days', value: '3' },
  { label: 'Yesterday (in the past)', value: '-1' },
];

/** Half-hour slots across a working day. */
const TIMES = withDefault(
  Array.from({ length: 25 }, (_, slot) => {
    const minutes = 7 * 60 + slot * 30;
    return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
  }),
);

const pad = (n: number) => String(n).padStart(2, '0');
const formDate = (offsetDays: number) => {
  const date = new Date(Date.now() + offsetDays * 24 * 60 * 60 * 1000);
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
};

type Field =
  | 'requestType'
  | 'pickUpSite'
  | 'destinationSite'
  | 'day'
  | 'time'
  | 'urgent'
  | 'wheelchair'
  | 'accessVehicle'
  | 'requester'
  | 'department';

interface IProps {
  options: IDeliveryRequestFieldOptions;
  onChange: (overrides: Record<string, unknown>) => void;
}

const RequestFieldOverrides = ({ options, onChange }: IProps) => {
  const [chosen, setChosen] = useState<Partial<Record<Field, string>>>({});
  const [pickUpPostcode, setPickUpPostcode] = useState('');
  const [destinationPostcode, setDestinationPostcode] = useState('');

  const sites = useMemo(() => withDefault([...options.sites, options.otherSite]), [options]);

  const overrides = useMemo(() => {
    const result: Record<string, unknown> = {};
    if (chosen.requestType) result.requestType = chosen.requestType;
    if (chosen.pickUpSite) {
      result.pickUpSite = chosen.pickUpSite;
      if (chosen.pickUpSite === options.otherSite) result.pickUpOtherPostcode = pickUpPostcode;
    }
    if (chosen.destinationSite) {
      result.destinationSite = chosen.destinationSite;
      if (chosen.destinationSite === options.otherSite) result.destinationOtherPostcode = destinationPostcode;
    }
    if (chosen.day) result.datePickUpRequired = formDate(Number(chosen.day));
    if (chosen.time) result.timePickUpRequired = chosen.time;
    if (chosen.urgent) result.urgent = chosen.urgent === 'Yes';
    if (chosen.wheelchair) result.wheelchairRequired = chosen.wheelchair;
    if (chosen.accessVehicle) result.wheelchairAccessVehicleRequired = chosen.accessVehicle;
    if (chosen.department) result.department = chosen.department;

    const requester = options.requesters.find((person) => person.name === chosen.requester);
    if (requester) {
      result.respondentName = requester.name;
      result.yourName = requester.name;
      result.yourEmail = requester.email;
      result.contactNumber = requester.phone;
      result.managersEmail = requester.manager;
    }
    return result;
  }, [chosen, pickUpPostcode, destinationPostcode, options]);

  useEffect(() => onChange(overrides), [overrides, onChange]);

  const select = (field: Field, label: string, choices: IOption[]) => (
    <div>
      <label className='body-01 mb-1 block font-semibold text-gray-700'>{label}</label>
      <Select
        options={choices}
        value={choices.find((choice) => choice.value === (chosen[field] ?? '')) ?? DEFAULT}
        onChange={(option) => setChosen((current) => ({ ...current, [field]: (option as IOption).value }))}
        testId={`override-${field}`}
      />
    </div>
  );

  return (
    <div className='grid grid-cols-1 gap-4 md:grid-cols-3'>
      {select('requestType', 'Request type', withDefault(options.requestTypes))}
      {select('requester', 'Requester', withDefault(options.requesters.map((person) => person.name)))}
      {select('department', 'Department', withDefault(options.departments))}

      <div>
        {select('pickUpSite', 'Collect from', sites)}
        {chosen.pickUpSite === options.otherSite && (
          <div className='mt-2'>
            <Textfield
              placeholder='Postcode, e.g. DE24 8HR'
              value={pickUpPostcode}
              onChange={(event) => setPickUpPostcode((event.target as HTMLInputElement).value)}
            />
          </div>
        )}
      </div>
      <div>
        {select('destinationSite', 'Deliver to', sites)}
        {chosen.destinationSite === options.otherSite && (
          <div className='mt-2'>
            <Textfield
              placeholder='Postcode, e.g. WS14 9UY'
              value={destinationPostcode}
              onChange={(event) => setDestinationPostcode((event.target as HTMLInputElement).value)}
            />
          </div>
        )}
      </div>
      {select('urgent', 'Urgent', YES_NO)}

      {select('day', 'Collection day', DAYS)}
      {select('time', 'Collection time', TIMES)}
      <div />

      {select('wheelchair', 'Wheelchair required', YES_NO)}
      {select('accessVehicle', 'Wheelchair access vehicle', YES_NO)}
    </div>
  );
};

export default RequestFieldOverrides;
